import { useState, useMemo, useCallback, useEffect } from 'react';
import { useDebounce } from 'use-debounce';

export interface UseOptimizedDataOptions<T> {
  data: T[];
  searchFields?: (keyof T)[];
  initialPageSize?: number;
  initialSortConfig?: { key: keyof T; direction: 'asc' | 'desc' };
}

export interface FilterConfig {
  id: string;
  value: any;
  type: 'text' | 'select' | 'date' | 'dateRange' | 'multiSelect';
}

export function useOptimizedData<T extends Record<string, any>>({
  data,
  searchFields = [],
  initialPageSize = 10,
  initialSortConfig
}: UseOptimizedDataOptions<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [sortConfig, setSortConfig] = useState(initialSortConfig);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // تأخير البحث لتحسين الأداء
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  // تطبيق البحث والفلترة
  const filteredData = useMemo(() => {
    let result = [...data];

    // تطبيق البحث النصي
    if (debouncedSearchQuery && searchFields.length > 0) {
      const query = debouncedSearchQuery.toLowerCase();
      result = result.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          return value?.toString().toLowerCase().includes(query);
        })
      );
    }

    // تطبيق الفلاتر
    filters.forEach(filter => {
      if (filter.value === null || filter.value === undefined || filter.value === '') {
        return;
      }

      result = result.filter(item => {
        const itemValue = item[filter.id];
        
        switch (filter.type) {
          case 'text':
            return itemValue?.toString().toLowerCase().includes(filter.value.toLowerCase());
          
          case 'select':
            return itemValue === filter.value;
          
          case 'multiSelect':
            if (Array.isArray(filter.value)) {
              return filter.value.includes(itemValue);
            }
            return itemValue === filter.value;
          
          case 'date':
            const itemDate = new Date(itemValue);
            const filterDate = new Date(filter.value);
            return itemDate.toDateString() === filterDate.toDateString();
          
          case 'dateRange':
            const { from, to } = filter.value;
            const itemDateRange = new Date(itemValue);
            return itemDateRange >= new Date(from) && itemDateRange <= new Date(to);
          
          default:
            return true;
        }
      });
    });

    return result;
  }, [data, debouncedSearchQuery, searchFields, filters]);

  // تطبيق الترتيب
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const { key, direction } = sortConfig;
    return [...filteredData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal, 'ar');
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        // محاولة التعامل مع التواريخ
        const aStr = String(aVal);
        const bStr = String(bVal);
        const aDate = new Date(aStr);
        const bDate = new Date(bStr);
        
        // التحقق من صحة التاريخ
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          comparison = aDate.getTime() - bDate.getTime();
        } else {
          comparison = aStr.localeCompare(bStr, 'ar');
        }
      }

      return direction === 'desc' ? -comparison : comparison;
    });
  }, [filteredData, sortConfig]);

  // تطبيق التصفح
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, pageSize]);

  // معلومات التصفح
  const pagination = useMemo(() => ({
    current: currentPage,
    pageSize,
    total: sortedData.length,
    totalPages: Math.ceil(sortedData.length / pageSize)
  }), [currentPage, pageSize, sortedData.length]);

  // إضافة فلتر
  const addFilter = useCallback((filterId: string, value: any, type: FilterConfig['type']) => {
    setFilters(prev => {
      const existingIndex = prev.findIndex(f => f.id === filterId);
      const newFilter: FilterConfig = { id: filterId, value, type };
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newFilter;
        return updated;
      } else {
        return [...prev, newFilter];
      }
    });
    setCurrentPage(1); // العودة إلى الصفحة الأولى عند إضافة فلتر
  }, []);

  // حذف فلتر
  const removeFilter = useCallback((filterId: string) => {
    setFilters(prev => prev.filter(f => f.id !== filterId));
    setCurrentPage(1);
  }, []);

  // مسح جميع الفلاتر
  const clearFilters = useCallback(() => {
    setFilters([]);
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  // تغيير الترتيب
  const changeSort = useCallback((key: keyof T, direction?: 'asc' | 'desc') => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        if (prev.direction === 'asc' && !direction) {
          return { key, direction: 'desc' };
        } else if (prev.direction === 'desc' && !direction) {
          return null; // إلغاء الترتيب
        }
      }
      return { key, direction: direction || 'asc' };
    });
    setCurrentPage(1);
  }, []);

  // تغيير الصفحة
  const changePage = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // إعادة تعيين الصفحة عند تغيير البيانات
  useEffect(() => {
    if (currentPage > pagination.totalPages && pagination.totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, pagination.totalPages]);

  return {
    // البيانات
    data: paginatedData,
    allData: sortedData,
    originalData: data,
    
    // البحث
    searchQuery,
    setSearchQuery,
    
    // الفلاتر
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    
    // الترتيب
    sortConfig,
    changeSort,
    
    // التصفح
    pagination,
    changePage,
    
    // الإحصائيات
    stats: {
      total: data.length,
      filtered: sortedData.length,
      showing: paginatedData.length
    }
  };
}